import React from 'react';

import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { usePlatesByType } from 'client/app/api/PlateTypesApi';
import { ExtraPlateDescriptionProps } from 'client/app/components/Parameters/PlateDescriptionsEditor/lib/sharedTypes';
import useWorkflowElementParameters from 'client/app/components/Parameters/PlateDescriptionsEditor/lib/useWorkflowElementParameters';
import PlateDescriptionEditorPanel from 'client/app/components/Parameters/PlateDescriptionsEditor/PlateDescriptionEditorPanel';
import { pluralize } from 'common/lib/format';
import {
  defaultPlateSetDescription,
  PlateSetDescription,
} from 'common/types/plateSetDescription';
import Colors from 'common/ui/Colors';
import Button from 'common/ui/components/Button';
import TextField from 'common/ui/filaments/TextField';
import { useAdditionalPanelContext } from 'common/ui/providers/AdditionalPanelProvider';

type Props = {
  value: PlateSetDescription | null;
  onChange: (value: PlateSetDescription | null) => void;
  isDisabled: boolean;
  extraProps: ExtraPlateDescriptionProps;
  isActive?: boolean;
  warningSlot?: JSX.Element;
};

/**
 * PlateDescriptionParameter represents the plate name and button that launches
 * the PlateDescriptionContents in a panel or dialog
 *
 * Dependencies:
 *  - Workflow builder state for querying other parameters in element instance
 *  - AdditionalPanelContext
 */
export default function PlateDescriptionParameter({
  value,
  onChange,
  isDisabled,
  extraProps,
  isActive = false,
  warningSlot,
}: Props) {
  const { isLayoutOptimised } = useWorkflowElementParameters(
    extraProps.elementInstanceId,
  );
  const plateDescription = value ?? defaultPlateSetDescription();
  const { setAdditionalPanel, clearAdditionalPanel } = useAdditionalPanelContext();
  const [getPlateType] = usePlatesByType();

  const handleClick = () =>
    setAdditionalPanel({
      id: plateDescription.id,
      contents: (
        <PlateDescriptionEditorPanel
          value={plateDescription}
          onChange={onChange}
          isDisabled={isDisabled}
          extraProps={extraProps}
          onClose={clearAdditionalPanel}
        />
      ),
    });

  const isPlateMissing =
    plateDescription.plateTypeName !== '' &&
    !getPlateType(plateDescription.plateTypeName);

  let buttonText = 'Edit plate description';
  if (plateDescription.plateTypeName) {
    buttonText = plateDescription.plateTypeName;
    if (!isLayoutOptimised) {
      buttonText += `, ${getWellCountCopy(plateDescription)} selected`;
    }
  }

  return (
    <Stack gap={2}>
      <TextField
        value={plateDescription.name}
        onChange={e => onChange({ ...plateDescription, name: e.target.value ?? '' })}
        placeholder="Plate name"
        disabled={isDisabled}
      />
      <EditButton
        variant="secondary"
        disabled={plateDescription.name === ''}
        onClick={handleClick}
        active={isActive}
      >
        <BreakWord variant="body2">{buttonText}</BreakWord>
      </EditButton>
      {isPlateMissing && (
        <FormHelperText error>
          This plate type does not exist in the plate library
        </FormHelperText>
      )}
      {warningSlot ?? null}
    </Stack>
  );
}

const EditButton = styled(Button, { shouldForwardProp: prop => prop !== 'active' })<{
  active: boolean;
}>(({ active, theme }) => ({
  padding: theme.spacing(3, 4),
  ...(active
    ? {
        background: Colors.BLUE_5,
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
      }
    : {}),
}));

const BreakWord = styled(Typography)({
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
});

function getWellCountCopy(plateDescription: PlateSetDescription) {
  const count = Object.values(plateDescription.regions).reduce<number>(
    (count, region) => count + region.wells.length,
    0,
  );
  return pluralize(count, 'well');
}
